"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Copy, Globe2, Info, Loader2, Plus, Share, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDeleteChatShare, useDeleteOrgChatShare, useShareChat } from "@/hooks/useChats";
import { useApi } from "@/lib/fetch-client";
import { useI18n } from "@llmgateway/i18n/react";
import type { Organization } from "@/lib/types";
interface ShareChatDialogProps {
  currentChatId: string;
  disabled?: boolean;
  shareId: string | null;
  orgShares: Array<{
    id: string;
    organizationId: string;
  }>;
  organizations: Organization[];
  chatTitle?: string | null;
  previewPrompt?: string | null;
}
type ShareMode = "public" | "organization";
function clipPreview(text: string | null | undefined, max: number): string | null {
  if (!text) {
    return null;
  }
  const flat = text.replace(/\s+/g, " ").trim();
  if (!flat) {
    return null;
  }
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}
interface ShareVisibilityIndicatorProps {
  label: string;
  shared: boolean;
}
function ShareVisibilityIndicator(t0) {
  const $ = _c(3);
  const {
    label,
    shared
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== shared) {
    t1 = shared ? <span className="flex shrink-0 items-center gap-2"><Check className="size-4 text-emerald-500" /><span className="sr-only">{label}</span></span> : null;
    $[0] = label;
    $[1] = shared;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function ShareChatDialog({
  currentChatId,
  disabled = true,
  shareId,
  orgShares,
  organizations,
  chatTitle,
  previewPrompt
}: ShareChatDialogProps) {
  const {
    t
  } = useI18n();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [createdShareUrl, setCreatedShareUrl] = useState<string | null>(null);
  // orgId → shareId for all known org shares (from props + created in session)
  const [orgShareMap, setOrgShareMap] = useState<Record<string, string>>(() => Object.fromEntries(orgShares.map(s => [s.organizationId, s.id])));
  const [shareMode, setShareMode] = useState<ShareMode>("public");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);
  const wasOpenRef = useRef(false);
  const queryClient = useQueryClient();
  const api = useApi();
  const shareChat = useShareChat();
  const deleteShare = useDeleteChatShare();
  const deleteOrgShare = useDeleteOrgChatShare(currentChatId);
  const shareUrl = useMemo(() => {
    if (!shareId || typeof window === "undefined") {
      return "";
    }
    return `${window.location.origin}/share/${shareId}`;
  }, [shareId]);
  const activeShareUrl = createdShareUrl ?? shareUrl;
  const previewTitle = useMemo(() => {
    const trimmed = chatTitle?.trim();
    if (trimmed) {
      return trimmed;
    }
    return clipPreview(previewPrompt, 80) ?? t("share.sharedChat");
  }, [chatTitle, previewPrompt, t]);
  const previewText = useMemo(() => clipPreview(previewPrompt, 160), [previewPrompt]);
  useEffect(() => {
    if (shareUrl) {
      setCreatedShareUrl(null);
    }
  }, [shareUrl]);
  useEffect(() => {
    setOrgShareMap(Object.fromEntries(orgShares.map(s_0 => [s_0.organizationId, s_0.id])));
    setCreatedShareUrl(null);
  }, [currentChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOrgShareMap(Object.fromEntries(orgShares.map(s_1 => [s_1.organizationId, s_1.id])));
  }, [orgShares]);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setCopied(false);
      setShareMode("public");
      setSelectedOrgId("");
    }
    wasOpenRef.current = open;
  }, [open]);
  useEffect(() => {
    if (organizations.length === 0 && shareMode === "organization") {
      setShareMode("public");
    }
  }, [organizations.length, shareMode]);
  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("share.linkCopied"));
    } catch {
      toast.error(t("share.failedCopyShareLink"));
    }
  };
  const createShare = async () => {
    const data = await shareChat.mutateAsync({
      params: {
        path: {
          id: currentChatId
        }
      },
      body: {}
    });
    const url_0 = `${window.location.origin}${data.share.url}`;
    setCreatedShareUrl(url_0);
    await copyLink(url_0);
  };
  const createOrgShare = async () => {
    if (!selectedOrgId) {
      toast.error(t("share.selectOrganization"));
      return;
    }
    const data_0 = await shareChat.mutateAsync({
      params: {
        path: {
          id: currentChatId
        }
      },
      body: {
        organizationId: selectedOrgId
      }
    });
    setOrgShareMap(prev => ({
      ...prev,
      [selectedOrgId]: data_0.share.id
    }));
    setSelectedOrgId("");
    toast.success(t("share.sharedWithOrganization"));
  };
  const deleteSharedLink = async () => {
    await deleteShare.mutateAsync({
      params: {
        path: {
          id: currentChatId
        }
      }
    });
    setCreatedShareUrl(null);
  };
  const deleteOrganizationShare = async (orgId: string, shareId_0: string) => {
    setDeletingOrgId(orgId);
    try {
      await deleteOrgShare.mutateAsync({
        params: {
          path: {
            shareId: shareId_0
          }
        }
      });
      const orgSharesQueryKey = api.queryOptions("get", "/chats/org/{organizationId}/shares", {
        params: {
          path: {
            organizationId: orgId
          }
        }
      }).queryKey;
      void queryClient.invalidateQueries({
        queryKey: orgSharesQueryKey
      });
      setOrgShareMap(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== orgId)));
    } finally {
      setDeletingOrgId(null);
    }
  };
  const isPublicShared = Boolean(shareId || createdShareUrl);
  const isOrganizationShared = Object.keys(orgShareMap).length > 0;
  const isAnyShared = Boolean(shareId || createdShareUrl || isOrganizationShared);
  const isCreatingSelectedMode = shareChat.isPending;
  const sharedOrgEntries = Object.entries(orgShareMap).map(([orgId_0, sId]) => ({
    orgId: orgId_0,
    shareId: sId,
    name: organizations.find(o => o.id === orgId_0)?.name ?? orgId_0
  }));
  const tooltipText = disabled ? t("share.shareAfterResponse") : isAnyShared ? t("share.shareActive") : t("share.shareChat");
  return <Dialog open={open} onOpenChange={nextOpen => {
    if (disabled && nextOpen) {
      return;
    }
    setOpen(nextOpen);
  }}>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="inline-flex" tabIndex={disabled ? 0 : undefined}>
						<DialogTrigger asChild>
							<Button type="button" variant={isAnyShared ? "secondary" : "ghost"} size="icon-sm" className="relative" disabled={disabled} aria-label={t("share.shareChat")}>
								<Share className="size-4" />
								{isAnyShared ? <span className="bg-primary absolute right-1 top-1 size-1.5 rounded-full" /> : null}
							</Button>
						</DialogTrigger>
					</span>
				</TooltipTrigger>
				<TooltipContent>
					<p>{tooltipText}</p>
				</TooltipContent>
			</Tooltip>
			<DialogContent className="max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[520px] min-w-0 gap-0 overflow-hidden overflow-y-auto p-0 sm:w-[calc(100vw-2rem)]">
				<DialogHeader className="min-w-0 px-4 pt-5 text-left sm:px-6 sm:pt-6">
					<DialogTitle className="pb-2 pr-8 text-left text-lg font-semibold sm:text-xl">
						{previewTitle}
					</DialogTitle>
				</DialogHeader>
				<div className="min-w-0 px-4 py-4 sm:px-6">
					<div className="border-border overflow-hidden rounded-lg border">
						<button type="button" className={["flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left transition-colors", shareMode === "public" ? "bg-muted/60" : "hover:bg-muted/40"].join(" ")} onClick={() => setShareMode("public")}>
							<Globe2 className="text-muted-foreground size-4 shrink-0" />
							<span className="min-w-0 flex-1">
								<span className="block text-sm font-medium">
									{t("share.public")}
								</span>
								<span className="text-muted-foreground block text-sm">
									{t("share.publicDescription")}
								</span>
							</span>
							<ShareVisibilityIndicator label={t("share.alreadyShared")} shared={isPublicShared} />
						</button>
						<button type="button" disabled={organizations.length === 0} className={["flex w-full min-w-0 items-center gap-3 border-t px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50", shareMode === "organization" ? "bg-muted/60" : "hover:bg-muted/40"].join(" ")} onClick={() => {
            if (organizations.length > 0) {
              setShareMode("organization");
            }
          }}>
							<Building2 className="text-muted-foreground size-4 shrink-0" />
							<span className="min-w-0 flex-1">
								<span className="block text-sm font-medium">
									{t("share.organization")}
								</span>
								<span className="text-muted-foreground block text-sm">
									{t("share.organizationDescription")}
								</span>
							</span>
							<ShareVisibilityIndicator label={t("share.alreadyShared")} shared={isOrganizationShared} />
						</button>
					</div>
				</div>
				{shareMode === "public" && isPublicShared && activeShareUrl ? <div className="min-w-0 space-y-5 px-4 pb-5 sm:px-6 sm:pb-6">
						<div className="bg-muted/60 border-border/60 relative overflow-hidden rounded-2xl border p-4 sm:p-5">
							{previewText ? <p className="text-foreground/90 line-clamp-4 text-sm leading-relaxed sm:text-[15px]">
									{previewText}
								</p> : <p className="text-muted-foreground text-sm">
									{t("share.snapshotVisible")}
								</p>}
						</div>
						<div className="bg-muted/40 border-border/60 flex min-w-0 items-center gap-2 rounded-full border py-2 pl-3 pr-2">
							<a href={activeShareUrl} target="_blank" rel="noopener noreferrer" className="text-foreground block min-w-0 flex-1 truncate text-sm" title={activeShareUrl}>
								{activeShareUrl}
							</a>
							<Button type="button" size="sm" variant="ghost" className="h-8 min-w-8 shrink-0 rounded-full px-2" onClick={() => copyLink(activeShareUrl)} aria-label={copied ? t("share.linkCopied") : t("share.copyLink")}>
								{copied ? <Check className="size-4" /> : <Copy className="size-4" />}
							</Button>
						</div>
						<div className="grid min-w-0 grid-cols-1 gap-1.5 sm:gap-3">
							<button type="button" onClick={() => copyLink(activeShareUrl)} className="group flex min-w-0 w-full flex-col items-center gap-1.5 sm:gap-2">
								<span className="bg-foreground text-background flex size-10 shrink-0 items-center justify-center rounded-full transition-transform group-hover:scale-105 sm:size-14">
									{copied ? <Check className="size-5" /> : <Copy className="size-5" />}
								</span>
								<span className="text-foreground max-w-full truncate text-xs font-medium sm:text-sm">
									{copied ? t("share.copied") : t("share.copyLink")}
								</span>
							</button>
						</div>
						<div className="text-muted-foreground flex gap-2 text-xs leading-relaxed">
							<Info className="mt-0.5 size-3.5 shrink-0" />
							<p className="min-w-0">{t("share.publicInfo")}</p>
						</div>
						<div className="flex justify-center">
							<Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive h-8" disabled={deleteShare.isPending} onClick={deleteSharedLink}>
								{deleteShare.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
								{deleteShare.isPending ? t("skills.deleting") : t("share.deleteSharedLink")}
							</Button>
						</div>
					</div> : shareMode === "organization" ? <div className="min-w-0 space-y-3 px-4 pb-5 sm:px-6 sm:pb-6">
						<div className="flex min-w-0 gap-2">
							<Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
								<SelectTrigger className="min-w-0 flex-1">
									<SelectValue placeholder={t("share.selectOrganization")} />
								</SelectTrigger>
								<SelectContent>
									{organizations.map(org => <SelectItem key={org.id} value={org.id} disabled={Boolean(orgShareMap[org.id])}>
											{org.name}
										</SelectItem>)}
								</SelectContent>
							</Select>
							<Button type="button" size="icon" disabled={!selectedOrgId || Boolean(orgShareMap[selectedOrgId]) || isCreatingSelectedMode} onClick={createOrgShare} aria-label={t("share.addOrganization")} className="sm:w-auto sm:px-4">
								{isCreatingSelectedMode ? <Loader2 className="size-4 animate-spin" /> : <>
										<Plus className="size-4 sm:hidden" />
										<span className="hidden sm:inline">{t("share.add")}</span>
									</>}
							</Button>
						</div>
						{sharedOrgEntries.length > 0 ? <div className="border-border min-w-0 overflow-hidden rounded-lg border">
								{sharedOrgEntries.map(({
            orgId: orgId_1,
            shareId: sId_0,
            name
          }) => <div key={orgId_1} className="flex min-w-0 items-center gap-3 px-3 py-2 text-sm [&:not(:first-child)]:border-t">
										<span className="min-w-0 flex-1 truncate">{name}</span>
										<Button type="button" variant="ghost" size="icon-sm" className="text-destructive hover:text-destructive shrink-0" disabled={deletingOrgId !== null} onClick={() => deleteOrganizationShare(orgId_1, sId_0)} aria-label={t("share.removeOrganization", {
              name
            })}>
											{deletingOrgId === orgId_1 ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
										</Button>
									</div>)}
							</div> : null}
						<div className="text-muted-foreground flex gap-2 text-xs leading-relaxed">
							<Info className="mt-0.5 size-3.5 shrink-0" />
							<p>{t("share.organizationInfo")}</p>
						</div>
					</div> : <div className="min-w-0 space-y-4 px-5 pb-5 sm:px-6 sm:pb-6">
						<p className="text-muted-foreground text-sm">
							{t("share.publicCreateDescription")}
						</p>
						<div className="text-muted-foreground flex gap-2 text-xs leading-relaxed">
							<Info className="mt-0.5 size-3.5 shrink-0" />
							<p className="min-w-0">{t("share.publicPrivacyInfo")}</p>
						</div>
						<div className="flex justify-end">
							<Button type="button" disabled={shareChat.isPending} onClick={createShare}>
								{shareChat.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
								{shareChat.isPending ? t("common.creating") : t("share.createShareLink")}
							</Button>
						</div>
					</div>}
			</DialogContent>
		</Dialog>;
}